import { useState } from 'react';
import { Link } from 'react-router';
import styled, { css } from 'styled-components';
import { LuPanelLeftOpen, LuPanelLeftClose } from 'react-icons/lu';
import { flow } from './primitives';

/**
 * The three-column frame used on every step of the video flow: left = where am I (steps, project, runs),
 * center = the video, timeline and the strip under it, right = what matters on this step.
 * Below 1280 px the left column becomes a drawer; below 900 px everything stacks.
 */

const Page = styled.div`
	height: ${({ theme }) => theme.utils.fullHeight};
	display: flex;
	flex-direction: column;
	background: ${flow.page};
	color: ${flow.text};
	@media (max-width: 899.98px) {
		height: auto;
		min-height: ${({ theme }) => theme.utils.fullHeight};
	}
`;

const TopLine = styled.div`
	flex: none;
	display: flex;
	align-items: center;
	min-width: 0;
`;

const Crumbs = styled.nav`
	flex: 1;
	display: flex;
	align-items: center;
	gap: 0.625rem;
	padding: 0.75rem 1rem 0;
	font-size: 0.875rem;
	color: ${flow.text2};
	min-width: 0;
	a {
		color: ${flow.text2};
		text-decoration: none;
	}
	a:hover {
		color: ${flow.text};
	}
	b {
		color: ${flow.text};
		font-weight: 600;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	i {
		color: #4c514c;
		font-style: normal;
	}
`;

// the page's own controls (a tour's help button), at the far end of the breadcrumb line and padded like it
const Actions = styled.div`
	flex: none;
	display: flex;
	align-items: center;
	padding: ${({ theme }) => `${theme.token.paddingSM}px ${theme.token.padding}px 0 0`};
`;

const DrawerToggle = styled.button`
	display: none;
	border: 1px solid #3a3e3a;
	background: transparent;
	color: ${flow.text2};
	border-radius: 8px;
	padding: 0.25rem 0.5rem;
	@media (max-width: 1279.98px) and (min-width: 900px) {
		display: inline-flex;
	}
`;

const Columns = styled.div`
	flex: 1;
	min-height: 0;
	display: grid;
	grid-template-columns: ${({ $noLeft }) =>
		$noLeft ? 'minmax(0, 1fr) 352px' : '264px minmax(0, 1fr) 352px'};
	gap: 1rem;
	padding: 1rem;
	position: relative;
	@media (max-width: 1279.98px) {
		grid-template-columns: minmax(0, 1fr) 340px;
	}
	@media (max-width: 899.98px) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-height: 0;
	min-width: 0;
	overflow-y: auto;
	@media (max-width: 899.98px) {
		overflow: visible;
	}
`;

const Left = styled(Column)`
	@media (max-width: 1279.98px) and (min-width: 900px) {
		position: absolute;
		z-index: 5;
		top: 1rem;
		bottom: 1rem;
		left: 1rem;
		width: 280px;
		padding: 0.5rem;
		border-radius: ${flow.radius};
		background: #111311;
		box-shadow: 0 18px 50px rgba(0, 0, 0, 0.6);
		${({ $open }) =>
			!$open &&
			css`
				display: none;
			`}
	}
	@media (max-width: 899.98px) {
		order: 3;
	}
`;

const Centre = styled(Column)`
	gap: 0.75rem;
	overflow: hidden;
	@media (max-width: 899.98px) {
		order: 1;
		overflow: visible;
	}
`;

const Right = styled(Column)`
	@media (max-width: 899.98px) {
		order: 2;
	}
`;

/**
 * @param crumbs [{ title, link }] — the last entry is the current page (no link)
 * @param left the left column; leave it out and the center takes its width (the results page)
 * @param actions controls for the whole page, at the end of the breadcrumb line
 * @param status shown at the right end of the breadcrumb row (whether a GPU machine is starting or working)
 */
const VideoFlowShell = ({ crumbs = [], actions = null, left, centre, right, status = null }) => {
	const [drawer, setDrawer] = useState(false);
	const noLeft = left == null;
	return (
		<Page>
			<TopLine>
				<Crumbs aria-label="Breadcrumb">
					{!noLeft && (
						<DrawerToggle
							type="button"
							onClick={() => setDrawer(d => !d)}
							aria-expanded={drawer}
							aria-label={drawer ? 'Hide project panel' : 'Show project panel'}>
							{drawer ? <LuPanelLeftClose /> : <LuPanelLeftOpen />}
						</DrawerToggle>
					)}
					{crumbs.map((c, i) => {
						const last = i === crumbs.length - 1;
						return (
							<span key={`${c.title}-${i}`} style={{ display: 'contents' }}>
								{last ? (
									<b aria-current="page">{c.title}</b>
								) : c.link ? (
									<Link to={c.link}>{c.title}</Link>
								) : (
									<span>{c.title}</span>
								)}
								{!last && <i aria-hidden="true">›</i>}
							</span>
						);
					})}
					{status}
				</Crumbs>
				{actions && <Actions>{actions}</Actions>}
			</TopLine>
			<Columns $noLeft={noLeft}>
				{!noLeft && <Left $open={drawer}>{left}</Left>}
				<Centre>{centre}</Centre>
				<Right>{right}</Right>
			</Columns>
		</Page>
	);
};

export default VideoFlowShell;
